import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowUpRight, Sparkle } from "@phosphor-icons/react";

const CONTENT = {
  it: {
    title: "Alfio Moretti · La storia dietro Otelya",
    desc: "Come un ragazzo dell'Umbria, cresciuto tra agriturismi e piccole strutture familiari, ha deciso di costruire la receptionist AI multilingua per l'ospitalità italiana.",
    ogTitle: "La storia di Alfio · Founder di Otelya",
    heading1: "Da un agriturismo umbro",
    heading1em: "alla receptionist AI italiana.",
    subhead: "La storia di Alfio Moretti, fondatore di Otelya.",
    backHome: "Torna alla home",
    ctaTry: "Prova Otelya gratis",
    imageAlt: "Alfio Moretti, fondatore di Otelya",
    body: [
      { kind: "p", text: "Da sempre appassionato di natura, territorio e turismo. Non dalla Silicon Valley, non da una carriera in una big tech. Provengo da una famiglia umbra che ha visto passare, generazione dopo generazione, il modo in cui l'Italia accoglie chi viene a trovarci." },
      { kind: "p", text: "Ho lavorato e collaborato tra agriturismi, piccoli hotel, casali affittati settimana per settimana — quel mondo caldo, artigianale, profondamente umano che chiamiamo *ospitalità italiana*. E ho visto, con i miei occhi, la stessa scena ripetersi in decine di strutture diverse:" },
      { kind: "quote", text: "Un ospite tedesco scrive su WhatsApp alle 23 della domenica sera. La risposta arriva lunedì mattina alle 9. Quell'ospite ha già prenotato altrove." },
      { kind: "p", text: "Un albergatore di Perugia mi disse una volta: *\"Non sono le grandi catene a farmi paura. Sono le prenotazioni che perdo tra le 22 e le 8 del mattino, ogni singolo giorno dell'anno.\"* Quella frase mi è rimasta addosso per due anni." },
      { kind: "h", text: "Perché Otelya" },
      { kind: "p", text: "Otelya è la risposta a quella frase. Non è un chatbot. Non è \"AI generica applicata all'hospitality\". È una **receptionist multilingua** costruita specificamente per il modo in cui gli italiani accolgono: con il tono della singola struttura, con il contesto reale (orari, prezzi, servizi, regole), e con il tatto di sapere quando fermarsi e passare la parola a te." },
      { kind: "p", text: "Otelya risponde in oltre 30 lingue, 24 ore su 24, su ogni canale — email, WhatsApp, telefono, prenotazioni, sito web. Ma la vera magia è che gli ospiti stranieri non si accorgono di parlare con un'AI. Si sentono accolti da qualcuno che parla la loro lingua e conosce la casa in cui stanno per entrare." },
      { kind: "h", text: "Il nome" },
      { kind: "p", text: "Otelya non è un nome inventato a tavolino in una sessione di brainstorming corporate. Nasce da *hotel* + *Italia*, mescolati con la delicatezza di un nome femminile italiano — perché in Italia l'accoglienza è, storicamente, qualcosa di femminile, materno, di casa." },
      { kind: "h", text: "Cosa faccio ora" },
      { kind: "p", text: "Lavoro ogni giorno per rendere Otelya più intelligente, più italiana e più utile. Rispondo personalmente a ogni email di chi si registra. Chiamo io le prime strutture iscritte, uno alla volta, per capire cosa funziona e cosa manca." },
      { kind: "p", text: "Se hai una struttura ricettiva in Italia — grande o piccola, storica o nuova — e vuoi che Otelya lavori per te, scrivimi. Rispondo io, o Otelya. In entrambi i casi, la lingua è quella dell'ospitalità italiana." },
      { kind: "sign", text: "Con gratitudine,\n**Alfio Moretti**\nFondatore · Otelya" },
    ],
  },
  en: {
    title: "Alfio Moretti · The story behind Otelya",
    desc: "How a young man from Umbria, raised among farm stays and small family properties, decided to build the multilingual AI receptionist for Italian hospitality.",
    ogTitle: "Alfio's story · Otelya's founder",
    heading1: "From an Umbrian farm stay",
    heading1em: "to Italy's AI receptionist.",
    subhead: "The story of Alfio Moretti, founder of Otelya.",
    backHome: "Back home",
    ctaTry: "Try Otelya free",
    imageAlt: "Alfio Moretti, Otelya's founder",
    body: [
      { kind: "p", text: "I come from tourism. Not Silicon Valley, not a career in big tech. I come from an Umbrian family that has watched, generation after generation, the way Italy welcomes those who come to visit us." },
      { kind: "p", text: "I've worked among farm stays, small hotels, houses rented week by week — that warm, artisanal, deeply human world we call *Italian hospitality*. And I've seen, with my own eyes, the same scene repeat itself in dozens of different properties:" },
      { kind: "quote", text: "A German guest writes on WhatsApp at 11 PM on a Sunday night. The reply arrives Monday morning at 9. That guest has already booked elsewhere." },
      { kind: "p", text: "A Perugian hotelier once told me: *\"It's not the big chains I'm afraid of. It's the bookings I lose between 10 PM and 8 AM, every single day of the year.\"* That sentence stuck with me for two years." },
      { kind: "h", text: "Why Otelya" },
      { kind: "p", text: "Otelya is the answer to that sentence. It's not a chatbot. It's not \"generic AI applied to hospitality.\" It's a **multilingual receptionist** built specifically for the way Italians welcome: with the tone of the individual property, with the real context (hours, prices, services, rules), and with the tact of knowing when to stop and pass the word back to you." },
      { kind: "p", text: "Otelya replies in over 30 languages, 24 hours a day, on every channel — email, WhatsApp, phone, bookings, website. But the real magic is that international guests don't realize they're talking to an AI. They feel welcomed by someone who speaks their language and knows the home they're about to enter." },
      { kind: "h", text: "The name" },
      { kind: "p", text: "Otelya isn't a name invented in a corporate brainstorm. It's born from *hotel* + *Italy*, mixed with the softness of an Italian feminine name — because in Italy, hospitality has historically been something feminine, maternal, of home." },
      { kind: "h", text: "What I do now" },
      { kind: "p", text: "I work every day to make Otelya smarter, more Italian, and more useful. I personally reply to every email from those who sign up. I call the first pilot properties myself, one at a time, to understand what works and what's missing." },
      { kind: "p", text: "If you run a hospitality property in Italy — big or small, historic or new — and you want Otelya to work for you, write to me. Either I'll reply, or Otelya will. Either way, the language will be that of Italian hospitality." },
      { kind: "sign", text: "With gratitude,\n**Alfio Moretti**\nFounder · Otelya" },
    ],
  },
};

function useLang() {
  const params = new URLSearchParams(window.location.search);
  const q = params.get("lang");
  if (q === "en" || q === "it") return q;
  return (navigator.language || "it").toLowerCase().startsWith("en") ? "en" : "it";
}

export default function Founder() {
  const lang = useLang();
  const c = CONTENT[lang] || CONTENT.it;
  return (
    <div className="min-h-screen bg-canvas text-ink">
      <Helmet>
        <html lang={lang} />
        <title>{c.title}</title>
        <meta name="description" content={c.desc} />
        <link rel="canonical" href="https://otelya.app/founder" />
        <link rel="alternate" hrefLang="it-IT" href="https://otelya.app/founder" />
        <link rel="alternate" hrefLang="en-US" href="https://otelya.app/founder?lang=en" />
        <link rel="alternate" hrefLang="x-default" href="https://otelya.app/founder" />
        <meta property="og:type" content="profile" />
        <meta property="og:title" content={c.ogTitle} />
        <meta property="og:description" content={c.desc} />
        <meta property="og:url" content="https://otelya.app/founder" />
        <meta property="og:locale" content={lang === "en" ? "en_US" : "it_IT"} />
        <meta name="twitter:title" content={c.ogTitle} />
        <meta name="twitter:description" content={c.desc} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          "name": "Alfio Moretti",
          "jobTitle": lang === "en" ? "Founder of Otelya" : "Fondatore di Otelya",
          "url": "https://otelya.app/founder",
          "worksFor": {
            "@type": "Organization",
            "name": "Otelya",
            "url": "https://otelya.app/"
          },
          "nationality": "Italian",
          "homeLocation": { "@type": "Place", "name": "Perugia, Umbria, Italy" },
          "description": c.desc,
        })}</script>
      </Helmet>

      {/* Minimal Nav */}
      <nav className="border-b border-line/40">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link to="/" data-testid="founder-back-home">
            <Logo />
          </Link>
          <Link to={`/?lang=${lang}`} className="text-sm text-stone hover:text-terracotta transition-colors flex items-center gap-1">
            <ArrowLeft size={14} /> {c.backHome}
          </Link>
        </div>
      </nav>

      <article className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        {/* Kicker */}
        <div className="inline-flex items-center gap-2 rounded-full bg-sand px-3 py-1.5 text-xs tracking-wide text-stone uppercase">
          <Sparkle weight="fill" size={14} className="text-gold" />
          {lang === "en" ? "Founder's story" : "La storia del fondatore"}
        </div>

        {/* Headline */}
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink mt-6">
          {c.heading1} <em className="italic text-terracotta">{c.heading1em}</em>
        </h1>
        <p className="mt-6 text-lg text-stone leading-relaxed italic">{c.subhead}</p>

        {/* Founder portrait — full image visible (no crop) */}
        <figure className="mt-10 lg:mt-14 mx-auto max-w-[520px] rounded-[24px] overflow-hidden ring-1 ring-line shadow-[0_30px_80px_-40px_rgba(28,33,30,0.35)]" data-testid="founder-portrait-figure">
          <div className="aspect-[928/1152] bg-gradient-to-br from-sand via-canvas to-terracotta/20 relative">
            <img
              alt={c.imageAlt}
              src="/founder-portrait.jpg"
              className="w-full h-full object-contain object-center"
              data-testid="founder-portrait-img"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling.style.display = 'grid';
              }}
            />
            <div style={{display: 'none'}} className="absolute inset-0 place-items-center">
              <div className="text-center">
                <div className="w-32 h-32 rounded-full bg-terracotta/20 border-4 border-canvas grid place-items-center mx-auto">
                  <span className="font-serif text-5xl text-terracotta">A</span>
                </div>
                <p className="mt-4 text-xs text-stone/60 italic">
                  {lang === "en" ? "Alfio's portrait — coming soon" : "Ritratto di Alfio — in arrivo"}
                </p>
              </div>
            </div>
          </div>
          <figcaption className="text-xs text-stone p-4 bg-canvas border-t border-line/40 text-center">
            {lang === "en" ? "Alfio Moretti · Founder of Otelya · Tuscany, Italy" : "Alfio Moretti · Fondatore di Otelya · Toscana, Italia"}
          </figcaption>
        </figure>

        {/* Body */}
        <div className="mt-14 prose prose-lg max-w-none text-ink" data-testid="founder-body">
          {c.body.map((block, i) => {
            if (block.kind === "h") {
              return (
                <h2 key={i} className="font-serif text-2xl lg:text-3xl mt-14 mb-4 text-ink">
                  {block.text}
                </h2>
              );
            }
            if (block.kind === "quote") {
              return (
                <blockquote key={i} className="my-10 pl-6 border-l-4 border-terracotta font-serif text-xl italic text-ink leading-relaxed">
                  {block.text}
                </blockquote>
              );
            }
            if (block.kind === "sign") {
              return (
                <div key={i} className="mt-16 pt-8 border-t border-line/60">
                  <p className="text-ink whitespace-pre-line leading-relaxed">
                    {block.text.split(/\*\*(.+?)\*\*/g).map((part, j) => j % 2 === 1 ? <strong key={j}>{part}</strong> : part)}
                  </p>
                </div>
              );
            }
            // Regular paragraph — parse **bold** and *italic*
            const parts = [];
            let text = block.text;
            let idx = 0;
            text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).forEach((seg, j) => {
              if (seg.startsWith("**") && seg.endsWith("**")) parts.push(<strong key={j}>{seg.slice(2, -2)}</strong>);
              else if (seg.startsWith("*") && seg.endsWith("*")) parts.push(<em key={j}>{seg.slice(1, -1)}</em>);
              else parts.push(seg);
            });
            return (
              <p key={i} className="mt-6 text-base sm:text-lg text-ink leading-[1.75]">
                {parts}
              </p>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 pt-12 border-t border-line/60 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <Link to="/auth?mode=signup">
            <Button size="lg" className="rounded-full bg-ink text-canvas hover:bg-olive px-7 h-12" data-testid="founder-cta-signup">
              {c.ctaTry}
              <ArrowUpRight weight="bold" size={18} className="ml-1" />
            </Button>
          </Link>
          <a href="mailto:hello@otelya.app" className="text-sm text-stone hover:text-terracotta transition-colors">
            hello@otelya.app
          </a>
        </div>
      </article>
    </div>
  );
}
